import { CompanyTypeSlug } from './companies'
import { SavedView } from './saved_view'

export const WORKFLOW_STATUS = {
  DRAFT: 'draft',
  CONTACTS_REQUESTED: 'contacts_requested',
  READY_TO_LAUNCH: 'ready_to_launch',
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  COMPLETE: 'complete',
  PAUSED: 'paused',
} as const

export type WorkflowStatus =
  (typeof WORKFLOW_STATUS)[keyof typeof WORKFLOW_STATUS]

// Statuses where the campaign's email sequence has launched (or is queued
// to launch). Removing companies or contacts in these states alters a live
// sequence, so destructive actions must be confirmed (FIR-7549). Mirrors
// LIVE_SEQUENCE_WORKFLOW_STATUSES on server contacts/models.py — keep in sync.
export const LIVE_SEQUENCE_STATUSES: readonly WorkflowStatus[] = [
  WORKFLOW_STATUS.SCHEDULED,
  WORKFLOW_STATUS.ACTIVE,
  WORKFLOW_STATUS.PAUSED,
]

export function isSequenceLive(
  status: WorkflowStatus | null | undefined
): boolean {
  return !!status && LIVE_SEQUENCE_STATUSES.includes(status)
}

// Statuses before the sequence has ever launched. Not the complement of
// LIVE_SEQUENCE_STATUSES: `complete` is post-completion — the sequence ran —
// so it is neither pre-launch nor live (FIR-7990).
export const PRE_LAUNCH_STATUSES: readonly WorkflowStatus[] = [
  WORKFLOW_STATUS.DRAFT,
  WORKFLOW_STATUS.CONTACTS_REQUESTED,
  WORKFLOW_STATUS.READY_TO_LAUNCH,
]

/**
 * Campaign lifecycle phase for sequence-status DISPLAY and the add-to-sequence
 * action, keyed on "has the sequence started sending".
 *
 * Deliberately different from LIVE_SEQUENCE_STATUSES (removal confirmation,
 * FIR-7549), which counts `scheduled` as live because removing from a
 * scheduled campaign changes what will send. Here `scheduled` is
 * `not_launched`: nothing has been sent yet, so the queued / never-contacted
 * split is meaningless plumbing — same as a draft (FIR-7990).
 *
 * Returns `null` for unknown/missing status so callers apply a safe default
 * (show the real status; block the add action).
 */
export type SequencePhase = 'not_launched' | 'running' | 'completed'

const RUNNING_STATUSES: readonly WorkflowStatus[] = [
  WORKFLOW_STATUS.ACTIVE,
  WORKFLOW_STATUS.PAUSED,
]
const NOT_LAUNCHED_STATUSES: readonly WorkflowStatus[] = [
  ...PRE_LAUNCH_STATUSES,
  WORKFLOW_STATUS.SCHEDULED,
]

export function sequencePhase(
  status: WorkflowStatus | null | undefined
): SequencePhase | null {
  if (!status) return null
  if (RUNNING_STATUSES.includes(status)) return 'running'
  if (status === WORKFLOW_STATUS.COMPLETE) return 'completed'
  if (NOT_LAUNCHED_STATUSES.includes(status)) return 'not_launched'
  return null
}

export const WORKFLOW_STATUS_LABELS: Record<string, string> = {
  [WORKFLOW_STATUS.DRAFT]: 'Draft',
  [WORKFLOW_STATUS.CONTACTS_REQUESTED]: 'Contacts Requested',
  [WORKFLOW_STATUS.READY_TO_LAUNCH]: 'Ready to Launch',
  [WORKFLOW_STATUS.SCHEDULED]: 'Scheduled',
  [WORKFLOW_STATUS.ACTIVE]: 'Active',
  [WORKFLOW_STATUS.COMPLETE]: 'Complete',
  [WORKFLOW_STATUS.PAUSED]: 'Paused',
}

export type Campaign = {
  id: number
  company_id: number
  color: string

  name: string
  description: string
  type: {
    id: number
    name: string
    slug: CompanyTypeSlug
  } | null
  product?: {
    id: number
    name: string
  }

  created: string
  modified: string
  days_since_last_update: string

  days_since_sent: number | null
  days_since_reply: number | null

  contact_company_count: number
  door_count: number

  deal_count: number
  deal_perc: number
  deal_perc_won: number

  contact_count: number
  companies_with_contacts: number
  companies_with_contacts_perc: number
  taro: number
  taro_closed: number
  taro_est_meals_day: number

  workflow_status: WorkflowStatus
  launch_date: string | null
  actual_launch_date: string | null
  scheduled_launch_at: string | null
  auto_launch_failure_reason: string | null
  auto_launch_attempted_at: string | null
  owner_id: number | null
  owner_name: string | null
  // Users who receive this campaign's notification emails (e.g. interested
  // replies). Empty means the default: the campaign owner.
  notification_recipient_ids: number[]

  bison_campaign_id: number | null
  interested_rate: number | null
  reply_count: number | null
  unread_reply_count: number | null
  action_needed_count: number | null
  bounce_count: number | null
  // Emails sent for this campaign, sourced from BisonCampaign.emails_sent.
  // Null when the campaign has no linked Bison campaign (e.g. not yet launched).
  emails_sent: number | null
  is_archived: boolean
  // The saved target-table filter that produced this campaign's targets,
  // auto-saved on push-to-campaign and name-synced with the campaign
  // (FIR-7843). Null when the campaign has never been pushed to with filters.
  // target_count_at_push is the membership count stamped at the last
  // filter-carrying push (null on campaigns linked before drift tracking).
  saved_view:
    | (Pick<SavedView, 'id' | 'name' | 'identifier'> & {
        target_count_at_push: number | null
      })
    | null
}

/**
 * Number of targets added to the campaign OUTSIDE its source filter since the
 * last filtered push (manual adds, unfiltered pushes, automations) — keeps
 * the Source Filter chip honest (FIR-7843).
 *
 * Returns 0 when there is no linked filter, no stamped count (pre-tracking
 * campaigns), or when the live count is not above the stamp (removals can
 * push it below; that is not "added outside", so it clamps to 0).
 */
export function getSourceFilterDrift(
  campaign: Pick<Campaign, 'saved_view' | 'contact_company_count'>
): number {
  const stamped = campaign.saved_view?.target_count_at_push
  if (stamped == null) return 0
  return Math.max(0, campaign.contact_company_count - stamped)
}
